import { computed, ref } from 'vue'
import { api } from '@/global/services/api'
import { freezeObject } from '@/global/utils/utils'
import TabActionButtons from '@/global/components/view-layouts/tabs-layout/components/TabActionButtons.vue'
import TaskDetailsInfo from '@/modules/road-maintenance-module/winter-service-v2/tasks/components/TaskDetailsInfo.vue'
import { useTaskFields } from '@/modules/road-maintenance-module/winter-service-v2/tasks/composables/useTaskFields'
import { useTaskVehicleFacts } from '@/modules/road-maintenance-module/winter-service-v2/tasks/composables/useTaskVehicleFacts'
import { toTaskPayload } from '@/modules/road-maintenance-module/winter-service-v2/tasks/composables/taskData'
import { WINTER_SERVICE_MODULE } from '@/modules/road-maintenance-module/winter-service-v2/common/config/constants'
import {
  TASKS_ENDPOINT,
  TASKS_TO_REQUESTS_ENDPOINT
} from '@/modules/road-maintenance-module/winter-service-v2/tasks/config/constants'
import {
  DEPARTMENTS_AUTOCOMPLETE_ENDPOINT,
  REQUESTS_INDEX_ROUTE,
  REQUESTS_TRANSLATION_NAMESPACE as NS,
  REQUEST_BASIC_INFO_TAB_KEY,
  REQUEST_DETAILS_ROUTE
} from '@/modules/road-maintenance-module/winter-service-v2/requests/config/constants'

const COLUMNS_COUNT = 2

/**
 * The new request screen - the small, urgent kind: salt the bridges tonight.
 *
 * It is the same form as creating a task, because that is what it makes: a one-off task,
 * marked unplanned so it stays out of the tasks listing, and a request for it in the same
 * breath. From then on it only shows up among the dispatcher's requests, and once it has
 * been carried out only under the finished tasks.
 *
 * That is two calls, which is why the screen posts the task through the tab layout and
 * makes the request in `afterSave`: there is no task to ask about before the first one
 * has answered.
 */
export function useRequestCreate (options) {
  const { translate, router } = options || {}

  // Unplanned by definition, so the toggle the task screen offers has nothing to decide.
  // The read-only vehicle fields are left out here - this screen is about the request
  // that is made from the task, not about the vehicle's configuration.
  const { rows } = useTaskFields({
    translate,
    withUnplannedToggle: false,
    withVehicleFacts: false
  })

  const { clear: clearVehicleFacts } = useTaskVehicleFacts()

  // The facts are shared by every screen that picks a winter service vehicle, so a
  // fresh form starts with none - see useTaskCreate.
  clearVehicleFacts()

  // Neither belongs to the task - they describe the request that is made from it, so they
  // are held back here rather than sent to the task endpoint.
  const pendingSourceId = ref(null)
  const pendingPlannedStart = ref(null)

  const apiConfig = freezeObject({
    post: {
      module: WINTER_SERVICE_MODULE,
      route: TASKS_ENDPOINT
    }
  })

  const goToIndex = () => {
    router?.push({ name: REQUESTS_INDEX_ROUTE })
  }

  // What the request itself needs: who is asking, and when it should start.
  const requestRow = computed(() => ({
    type: 'fields',
    title: translate(`${NS}.request_data`),
    columnsCount: COLUMNS_COUNT,
    fields: [
      {
        key: 'source_id',
        label: translate(`${NS}.request_source`),
        type: 'autocomplete',
        autocomplete_options: {
          module: WINTER_SERVICE_MODULE,
          route: DEPARTMENTS_AUTOCOMPLETE_ENDPOINT
        },
        creatable: true,
        editable: true,
        visible: true
      },
      {
        key: 'planned_start',
        label: translate(`${NS}.planned_start`),
        type: 'datetime-picker',
        appendIcon: 'mdi-calendar-month',
        creatable: true,
        editable: true,
        visible: true
      }
    ]
  }))

  // See useTaskDetails - `rows` is what Content.vue reads to build the save payload, so
  // the fields have to live there.
  const infoBlock = computed(() => ({
    component: TaskDetailsInfo,
    visible: true,
    rows: [
      ...rows.value,
      requestRow.value
    ]
  }))

  const tabActions = computed(() => ({
    component: TabActionButtons,
    title: translate(`${NS}.new_request`),
    showEditButtons: true,
    showBackButton: {
      onGoBack: goToIndex
    },
    onCancelTabSave: goToIndex,
    visible: true
  }))

  const modifyDataBeforeSend = ({ copiedData }) => {
    const {
      source_id: sourceId,
      planned_start: plannedStart,
      ...taskData
    } = copiedData || {}

    pendingSourceId.value = sourceId ?? null
    pendingPlannedStart.value = plannedStart ?? null

    return {
      ...toTaskPayload(taskData),
      // Sent as 1 rather than true: the tab is posted as FormData and Laravel's `boolean`
      // rule rejects the string "true".
      is_unplanned: 1
    }
  }

  /**
   * The task exists now, so the request for it can be made. The tasks listing's
   * `Convert to requests` uses the same endpoint, so a request made here is the same
   * record as one made from a selection there.
   */
  const afterSave = async (context, data) => {
    const { id } = data || {}

    if (!id) {
      return
    }

    try {
      const response = await api(WINTER_SERVICE_MODULE).post(TASKS_TO_REQUESTS_ENDPOINT, {
        task_ids: [id],
        source_id: pendingSourceId.value,
        planned_start: pendingPlannedStart.value
      })

      const [requestId] = response?.data?.incoming_request_ids || []

      if (requestId) {
        router?.replace({
          name: REQUEST_DETAILS_ROUTE,
          params: {
            id: String(requestId)
          }
        })
        return
      }

      // The task was created but no request came back - the listing is the only honest
      // place to land, and the api layer has already reported why.
      goToIndex()
    }
    catch (exception) {
      console.error(exception)
      goToIndex()
    }
    finally {
      pendingSourceId.value = null
      pendingPlannedStart.value = null
    }
  }

  return computed(() => ({
    tabs: [
      {
        key: REQUEST_BASIC_INFO_TAB_KEY,
        title: translate(`${NS}.new_request`),
        apiConfig,
        afterSave,
        modifyDataBeforeSend,
        tabContent: [
          tabActions.value,
          infoBlock.value
        ]
      }
    ]
  }))
}
